import { useLayoutEffect } from "react";

/**
 * One IntersectionObserver for every scroll reveal on the site, after the
 * pattern in mundus-cras.
 *
 * A section opts in by putting `data-reveal` on an element (and optionally
 * `style={{ "--reveal-delay": "120ms" }}` for a stagger). The CSS in index.css
 * hides those elements only once `html.reveal-ready` is set, which this
 * component does after it has marked everything already on screen as revealed.
 * So the prerendered page paints complete, nothing visible blinks out when the
 * script arrives, and a visitor without JavaScript sees the whole page.
 *
 * It runs as a layout effect so the first pass happens before the browser
 * paints React's first commit.
 *
 * Each element is observed on its own, not per section: a tall section on a
 * phone no longer waits for a fifth of its height to scroll in before any of
 * it appears. On touch screens the trigger line sits below the viewport, so
 * content is already settling as it scrolls into view.
 */
const SELECTOR = "[data-reveal]:not(.is-in), [data-reveal-lines]:not(.is-in)";

const RevealRoot = () => {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce || !("IntersectionObserver" in window)) {
      // Nothing is ever hidden: `reveal-ready` is not set.
      return;
    }

    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const oneColumn = window.matchMedia("(max-width: 767.98px)").matches;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          // Stagger reads as lag when the items are stacked in one column.
          if (oneColumn) el.style.setProperty("--reveal-delay", "0ms");
          el.classList.add("is-in");
          observer.unobserve(el);
        }
      },
      { rootMargin: coarse ? "0px 0px 15% 0px" : "0px 0px -6% 0px", threshold: 0 }
    );

    // First pass, before anything is hidden: whatever is already on screen
    // (or scrolled past) is shown as-is, so the prerendered paint never blinks.
    const viewportBottom = window.innerHeight;
    document.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
      if (el.getBoundingClientRect().top < viewportBottom) {
        el.classList.add("is-in");
      } else {
        observer.observe(el);
      }
    });
    root.classList.add("reveal-ready");

    // Sections mounted later (route changes, filters, tabs) carry their own
    // reveal targets; pick them up as they arrive.
    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches(SELECTOR)) observer.observe(node);
          node.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => observer.observe(el));
        });
      }
    });
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutations.disconnect();
      observer.disconnect();
      root.classList.remove("reveal-ready");
    };
  }, []);

  return null;
};

export default RevealRoot;
